import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { History, Loader2, ArrowRight, User, Clock, FileText, Printer, Download, Trash2 } from "lucide-react";
import { getFlightHistory, listFlightDerogations, getFlightDerogation, listFlightDocuments, getFlightDocument, deleteFlightDocument } from "@/lib/api";
import { usePermissions } from "@/context/PermissionContext";
import { toast } from "sonner";

const b64ToBlobUrl = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const formatHistoryDateTime = (isoString) => {
  if (!isoString) return "N/A";
  try {
    const d = new Date(isoString);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (val === true) return "Yes";
  if (val === false) return "No";
  return String(val);
};

const isEmptyVal = (v) => v === null || v === undefined || v === "" || v === false;

// Hide changes where both old & new are effectively empty (e.g. unset "—" -> "No")
const meaningfulChanges = (changes) =>
  (changes || []).filter((c) => !(isEmptyVal(c.old) && isEmptyVal(c.new)));

export const FlightHistoryButton = ({ flight, variant = "full" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [derogations, setDerogations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const { isMasterAdmin } = usePermissions();

  const loadHistory = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const [res, derog, docs] = await Promise.all([
        getFlightHistory(flight.id),
        listFlightDerogations(flight.id).catch(() => ({ data: [] })),
        listFlightDocuments(flight.id).catch(() => ({ data: [] })),
      ]);
      const all = res.data || [];
      // Drop records that have no meaningful changes after filtering empty->empty diffs.
      // Also hide pure "derogation/document saved" log entries — those are shown in the
      // dedicated sections above (avoid duplicate info).
      const cleaned = all
        .map((r) => ({ ...r, changes: meaningfulChanges(r.changes).filter((c) => c.field !== "derogation" && c.field !== "document") }))
        .filter((r) => r.changes.length > 0);
      setRecords(cleaned);
      setDerogations(derog.data || []);
      setDocuments(docs.data || []);
    } catch (e) {
      setRecords([]);
      setDerogations([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const openDoc = async (fetcher, d, action) => {
    try {
      const r = await fetcher(flight.id, d.id);
      const url = b64ToBlobUrl(r.data.data);
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = d.filename || "document.pdf"; a.click();
      } else if (action === "print") {
        const w = window.open(url, "_blank");
        if (w) w.onload = () => w.print();
      } else {
        window.open(url, "_blank");
      }
    } catch {
      toast.error("Could not open that document");
    }
  };

  const openDerogation = (d, action) => openDoc(getFlightDerogation, d, action);
  const openDocument = (d, action) => openDoc(getFlightDocument, d, action);

  const handleDeleteDocument = async (d) => {
    try {
      await deleteFlightDocument(flight.id, d.id);
      setDocuments((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Document deleted");
    } catch {
      toast.error("Could not delete the document");
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={loadHistory}
          className="text-[#64748b] hover:text-purple-600 hover:bg-purple-50 h-8 w-8"
          title="Show History"
          data-testid={`view-history-${flight.id}`}
        >
          <History size={16} />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={loadHistory}
          className="flex items-center gap-2 text-[#0f172a] border-[#94a3b8] hover:bg-[#0f172a] hover:text-white"
          data-testid={`show-history-${flight.id}`}
        >
          <History size={14} />
          Show History
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="flight-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={18} className="text-purple-600" />
              Modification History
              <span className="text-sm font-normal text-[#64748b]">
                — {flight.flight_number || flight.registration || "Flight"}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 pr-1 -mr-1">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-[#64748b]">
                <Loader2 className="animate-spin mr-2" size={20} /> Loading history…
              </div>
            ) : (
              <>
                {derogations.length > 0 && (
                  <div className="mb-4 border border-red-200 rounded-lg p-4 bg-red-50" data-testid="history-derogations">
                    <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                      <FileText size={13} /> Saved Derogation Forms
                    </p>
                    <div className="space-y-1.5">
                      {derogations.map((d) => (
                        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#334155]">
                          <span className="flex items-center gap-2 flex-wrap">
                            <FileText size={13} className="text-red-500" /> v{d.version}
                            <span className="flex items-center gap-1"><Clock size={11} /> {formatHistoryDateTime(d.created_at)}</span>
                            <span className="flex items-center gap-1"><User size={11} /> {d.created_by}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openDerogation(d, "open")} data-testid={`history-derog-open-${d.id}`}>Open</Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => openDerogation(d, "print")} title="Print"><Printer size={12} /></Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => openDerogation(d, "download")} title="Download"><Download size={12} /></Button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {documents.length > 0 && (
                  <div className="mb-4 border border-amber-200 rounded-lg p-4 bg-amber-50" data-testid="history-documents">
                    <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                      <FileText size={13} /> Generated Documents (GENDEC / Manifest)
                      {documents.length > 2 && (
                        <span className="font-normal text-amber-600/80">— showing last 2 of {documents.length}</span>
                      )}
                    </p>
                    <div className="space-y-1.5">
                      {documents.slice(0, 2).map((d) => (
                        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#334155]">
                          <span className="flex items-center gap-2 flex-wrap">
                            <FileText size={13} className="text-amber-600" />
                            <span className="uppercase font-semibold">{d.doc_type}</span> v{d.version}
                            <span className="flex items-center gap-1"><Clock size={11} /> {formatHistoryDateTime(d.created_at)}</span>
                            <span className="flex items-center gap-1"><User size={11} /> {d.created_by}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openDocument(d, "open")} data-testid={`history-doc-open-${d.id}`}>Open</Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => openDocument(d, "print")} title="Print"><Printer size={12} /></Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => openDocument(d, "download")} title="Download"><Download size={12} /></Button>
                            {isMasterAdmin && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-100" onClick={() => handleDeleteDocument(d)} title="Delete (Master Admin)" data-testid={`history-doc-delete-${d.id}`}>
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {records.length === 0 && derogations.length === 0 && documents.length === 0 ? (
                  <div className="text-center py-12 text-[#64748b]" data-testid="flight-history-empty">
                    No modifications have been recorded for this flight yet.
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="flight-history-list">
                    {records.map((rec) => (
                      <div key={rec.id} className="border border-[#e2e8f0] rounded-lg p-4 bg-[#f8fafc]">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs text-[#475569]">
                          <span className="flex items-center gap-1.5 font-semibold">
                            <User size={13} /> {rec.changed_by || "Unknown"}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock size={13} /> {formatHistoryDateTime(rec.changed_at)}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {(rec.changes || []).map((c, idx) => (
                            <div key={idx} className="text-sm flex flex-wrap items-center gap-2" data-testid="flight-history-change">
                              <span className="font-semibold text-[#0f172a] min-w-[120px]">{c.label}:</span>
                              <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 line-through">{formatValue(c.old)}</span>
                              <ArrowRight size={14} className="text-[#94a3b8]" />
                              <span className="px-2 py-0.5 rounded bg-green-50 text-green-700 font-medium">{formatValue(c.new)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="flight-history-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
